<template>
  <div
    class="course-native-video"
    :class="{ 'course-native-video--landscape': landscapeFs }"
  >
    <div class="course-native-video__stage">
      <video
        ref="videoEl"
        :src="src"
        class="course-native-video__player"
        controls
        playsinline
        webkit-playsinline
        preload="metadata"
        x5-playsinline="true"
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="landscape"
        @loadedmetadata="onReady"
      >
        您的浏览器不支持视频播放
      </video>
    </div>
    <div class="course-native-video__actions">
      <button
        v-if="courseId"
        type="button"
        class="course-native-video__btn course-native-video__btn--primary"
        @click="openPlayPage"
      >
        全屏播放
      </button>
      <button type="button" class="course-native-video__btn" @click="enterLandscapeFullscreen">
        {{ landscapeFs ? '退出横屏' : '本页横屏' }}
      </button>
      <span v-if="hint" class="course-native-video__hint">{{ hint }}</span>
    </div>
  </div>
</template>

<script>
import {
  applyWechatVideoAttrs,
  exitNativeFullscreen,
  isWechatBrowser,
  stashCoursePlayPayload,
  tryLockLandscape,
  tryNativeVideoFullscreen,
  tryUnlockOrientation
} from '@/utils/wechatVideo'

export default {
  name: 'CourseNativeVideo',
  props: {
    src: {
      type: String,
      required: true
    },
    courseId: {
      type: [Number, String],
      default: 0
    },
    kpId: {
      type: [Number, String],
      default: 0
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      landscapeFs: false,
      hint: ''
    }
  },
  mounted() {
    this.onReady()
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    this.leaveLandscapeFullscreen(true)
  },
  methods: {
    onReady() {
      applyWechatVideoAttrs(this.$refs.videoEl)
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.landscapeFs) {
        this.leaveLandscapeFullscreen()
      }
    },
    openPlayPage() {
      const courseId = Number(this.courseId) || 0
      if (!courseId || !this.src) return
      stashCoursePlayPayload({
        src: this.src,
        title: this.title,
        courseId,
        kpId: Number(this.kpId) || 0
      })
      this.$router.push({
        path: `/knowledge/courses/play/${courseId}`,
        query: {
          kpId: this.kpId || undefined,
          title: this.title || undefined
        }
      })
    },
    async enterLandscapeFullscreen() {
      if (this.landscapeFs) {
        await this.leaveLandscapeFullscreen()
        return
      }
      const video = this.$refs.videoEl
      applyWechatVideoAttrs(video)
      const nativeOk = await tryNativeVideoFullscreen(video)
      await tryLockLandscape()
      this.landscapeFs = true
      document.body.classList.add('course-video-landscape-lock')
      if (isWechatBrowser() && !nativeOk) {
        this.hint = '本页横屏已打开；也可点「全屏播放」进专用页'
      } else {
        this.hint = '可旋转手机；点「退出横屏」返回'
      }
      try {
        await video.play()
      } catch (_) {
        /* ignore */
      }
    },
    async leaveLandscapeFullscreen(silent) {
      this.landscapeFs = false
      document.body.classList.remove('course-video-landscape-lock')
      await exitNativeFullscreen()
      await tryUnlockOrientation()
      if (!silent) this.hint = ''
    }
  }
}
</script>

<style scoped>
.course-native-video {
  width: 100%;
}

.course-native-video__stage {
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.course-native-video__player {
  display: block;
  width: 100%;
  max-width: 100%;
  min-height: 220px;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  background: #000;
}

.course-native-video__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.course-native-video__btn {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #303133;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
}

.course-native-video__btn--primary {
  border-color: #409eff;
  background: #409eff;
  color: #fff;
  font-weight: 600;
}

.course-native-video__btn:active {
  opacity: 0.9;
}

.course-native-video__hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.course-native-video--landscape {
  position: fixed;
  inset: 0;
  z-index: 10050;
  margin: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.course-native-video--landscape .course-native-video__stage {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 0;
  width: 100vh;
  height: 100vw;
  max-width: none;
  transform: translate(-50%, -50%) rotate(90deg);
  transform-origin: center center;
}

.course-native-video--landscape .course-native-video__player {
  width: 100%;
  height: 100%;
  min-height: 0;
  aspect-ratio: auto;
  object-fit: contain;
}

.course-native-video--landscape .course-native-video__actions {
  position: fixed;
  z-index: 10051;
  left: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  margin: 0;
}

.course-native-video--landscape .course-native-video__btn {
  background: rgba(255, 255, 255, 0.92);
  color: #303133;
}

@media (max-width: 900px) {
  .course-native-video__player {
    min-height: min(56vh, 420px);
    height: min(56vh, 420px);
    aspect-ratio: auto;
  }

  .course-native-video__btn--primary {
    flex: 1 1 auto;
    min-height: 44px;
    font-size: 15px;
  }
}
</style>

<style>
body.course-video-landscape-lock {
  overflow: hidden !important;
}
</style>
